package dev.wyns.ventrova.ui.web

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppEnvironment
import dev.wyns.ventrova.app.AppMessage
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.platform.displayUrl
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.action_call
import dev.wyns.ventrova.resources.action_route
import dev.wyns.ventrova.resources.action_website
import dev.wyns.ventrova.resources.back_to_list
import dev.wyns.ventrova.resources.ic_call
import dev.wyns.ventrova.resources.ic_chevron_left
import dev.wyns.ventrova.resources.ic_directions_filled
import dev.wyns.ventrova.resources.ic_favorite
import dev.wyns.ventrova.resources.ic_favorite_filled
import dev.wyns.ventrova.resources.ic_ios_share
import dev.wyns.ventrova.resources.ic_language
import dev.wyns.ventrova.resources.ic_location_on
import dev.wyns.ventrova.resources.opening_hours
import dev.wyns.ventrova.resources.report_action
import dev.wyns.ventrova.resources.report_question
import dev.wyns.ventrova.resources.walk_minutes_long
import dev.wyns.ventrova.ui.components.BannerStyle
import dev.wyns.ventrova.ui.components.FryOilBanner
import dev.wyns.ventrova.ui.components.LevelBanner
import dev.wyns.ventrova.ui.components.MenuDishes
import dev.wyns.ventrova.ui.components.MenuStyle
import dev.wyns.ventrova.ui.components.PlaceThumbnail
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.WeekHours
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.components.cuisineLabel
import dev.wyns.ventrova.ui.components.menuTitle
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.currentDayOfWeek
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.distanceLabel
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.openStatusText
import kotlin.math.roundToInt
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.stringResource

/** Place detail inside the sidebar, as in the web design. */
@Composable
internal fun WebDetail(item: PlaceItem, menu: List<MenuItem>?, env: AppEnvironment) {
    val place = item.place
    val actions = env.dependencies.externalActions
    val viewModel = env.viewModel
    Column(Modifier.fillMaxSize().background(VentrovaColors.Paper)) {
        Row(Modifier.fillMaxWidth().height(56.dp).padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(
                Modifier.height(34.dp).clip(RoundedCornerShape(8.dp)).handCursor().clickable { viewModel.closeDetail() }.padding(start = 6.dp, end = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                VIcon(Res.drawable.ic_chevron_left, size = 20.dp, tint = VentrovaColors.Ink)
                Text(stringResource(Res.string.back_to_list), style = bodyStyle(14.sp, FontWeight.Medium))
            }
            Box(Modifier.weight(1f))
            IconSquare(Res.drawable.ic_ios_share, VentrovaColors.Ink) { actions.share(place) }
            IconSquare(
                if (item.isFavorite) Res.drawable.ic_favorite_filled else Res.drawable.ic_favorite,
                if (item.isFavorite) VentrovaColors.Favorite else VentrovaColors.Ink,
            ) { viewModel.toggleFavorite(item.id) }
        }
        HorizontalDivider(color = VentrovaColors.Line)
        Column(Modifier.weight(1f).verticalScroll(rememberScrollState()).padding(bottom = 32.dp)) {
            Row(Modifier.fillMaxWidth().height(224.dp).padding(start = 12.dp, end = 12.dp, top = 12.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                PlaceThumbnail(place, Modifier.weight(2f).fillMaxHeight(), RoundedCornerShape(topStart = 12.dp, bottomStart = 12.dp, topEnd = 4.dp, bottomEnd = 4.dp), iconSize = 40.dp)
                Column(Modifier.weight(1f).fillMaxHeight(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    PlaceThumbnail(place, Modifier.weight(1f).fillMaxWidth(), RoundedCornerShape(topStart = 4.dp, topEnd = 12.dp, bottomStart = 4.dp, bottomEnd = 4.dp), showIcon = false, stripeAngle = 45)
                    PlaceThumbnail(place, Modifier.weight(1f).fillMaxWidth(), RoundedCornerShape(topStart = 4.dp, topEnd = 4.dp, bottomStart = 4.dp, bottomEnd = 12.dp), showIcon = false, stripeAngle = 90)
                }
            }
            // Lets the name, address, hours and dishes be selected and copied.
            SelectionContainer {
                Column(Modifier.padding(start = 22.dp, end = 22.dp, top = 18.dp)) {
                    Text(listOfNotNull(stringResource(place.category.label), place.cuisineLabel()).joinToString(" · "), style = bodyStyle(13.5.sp, color = VentrovaColors.Muted))
                    Text(place.name, style = displayStyle(30, lineHeight = 1.05f), modifier = Modifier.padding(top = 4.dp))
                    LevelBanner(place.veganLevel, Modifier.padding(top = 12.dp).fillMaxWidth(), style = BannerStyle.Web)
                    FryOilBanner(
                        place,
                        onReport = { viewModel.requestFryOilCheck(place.id) },
                        modifier = Modifier.padding(top = 8.dp).fillMaxWidth(),
                        style = BannerStyle.Web,
                        linkModifier = Modifier.handCursor(),
                    )
                    val status = openStatusText(item.openStatus)
                    val distance = item.distanceMeters
                    if (status != null || distance != null) {
                        Text(
                            buildAnnotatedString {
                                if (status != null) {
                                    withStyle(SpanStyle(color = status.color, fontWeight = FontWeight.SemiBold)) { append(status.status) }
                                    status.change?.let { append(" · $it") }
                                }
                                if (distance != null) {
                                    if (status != null) append("     ")
                                    append(distanceLabel(distance) + " · " + stringResource(Res.string.walk_minutes_long, (distance / 1000 * 13).roundToInt().coerceAtLeast(1)))
                                }
                            },
                            style = bodyStyle(14.sp, color = VentrovaColors.Secondary),
                            modifier = Modifier.padding(top = 10.dp),
                        )
                    }
                    DisableSelection {
                        Row(Modifier.padding(top = 16.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            DetailButton(Res.drawable.ic_directions_filled, stringResource(Res.string.action_route), primary = true, modifier = Modifier.weight(1f)) { actions.openDirections(place) }
                            place.phone?.let { phone ->
                                DetailButton(Res.drawable.ic_call, stringResource(Res.string.action_call), primary = false, modifier = Modifier.weight(1f)) { actions.call(phone) }
                            }
                            place.website?.let { url ->
                                DetailButton(Res.drawable.ic_language, stringResource(Res.string.action_website), primary = false, modifier = Modifier.weight(1f)) { actions.openWebsite(url) }
                            }
                        }
                    }
                    if (place.menu != null) {
                        Text(menuTitle(place), style = displayStyle(19, lineHeight = 1.1f), modifier = Modifier.padding(top = 22.dp, bottom = 8.dp))
                        MenuDishes(place, menu, MenuStyle.Web, linkModifier = Modifier.handCursor())
                    }
                    if (place.openingHours != null) {
                        Text(stringResource(Res.string.opening_hours), style = displayStyle(19), modifier = Modifier.padding(top = 22.dp, bottom = 8.dp))
                        WeekHours(item, currentDayOfWeek(), fontSize = 14.sp, rowPadding = 4.dp)
                    }
                    Column(Modifier.padding(top = 24.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        place.address.formatted()?.let { ContactLine(Res.drawable.ic_location_on, it, VentrovaColors.Ink, null) }
                        place.phone?.let { phone -> ContactLine(Res.drawable.ic_call, phone, VentrovaColors.Ink) { actions.call(phone) } }
                        place.website?.let { url -> ContactLine(Res.drawable.ic_language, displayUrl(url), VentrovaColors.Accent) { actions.openWebsite(url) } }
                    }
                    Text(
                        buildAnnotatedString {
                            append(stringResource(Res.string.report_question) + " ")
                            withStyle(SpanStyle(color = VentrovaColors.Accent, fontWeight = FontWeight.SemiBold)) { append(stringResource(Res.string.report_action)) }
                        },
                        style = bodyStyle(13.sp, color = VentrovaColors.Muted),
                        modifier = Modifier.padding(top = 18.dp).handCursor().clickable { viewModel.showMessage(AppMessage.Kind.NotAvailableYet) },
                    )
                }
            }
        }
    }
}

@Composable
private fun IconSquare(icon: DrawableResource, tint: Color, onClick: () -> Unit) {
    Box(Modifier.size(34.dp).clip(RoundedCornerShape(8.dp)).handCursor().clickable(onClick = onClick), contentAlignment = Alignment.Center) {
        VIcon(icon, size = 20.dp, tint = tint)
    }
}

@Composable
private fun DetailButton(icon: DrawableResource, label: String, primary: Boolean, modifier: Modifier, onClick: () -> Unit) {
    val shape = RoundedCornerShape(10.dp)
    Row(
        modifier
            .height(42.dp)
            .clip(shape)
            .background(if (primary) VentrovaColors.Ink else Color.Transparent)
            .border(1.dp, if (primary) VentrovaColors.Ink else VentrovaColors.LineStrong, shape)
            .handCursor()
            .clickable(onClick = onClick),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        VIcon(icon, size = 19.dp, tint = if (primary) Color.White else VentrovaColors.Ink)
        Text(label, style = bodyStyle(14.sp, FontWeight.SemiBold, if (primary) Color.White else VentrovaColors.Ink), modifier = Modifier.padding(start = 6.dp))
    }
}

@Composable
private fun ContactLine(icon: DrawableResource, text: String, color: Color, onClick: (() -> Unit)?) {
    Row(
        Modifier.let { if (onClick != null) it.handCursor().clickable(onClick = onClick) else it },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        VIcon(icon, size = 19.dp, tint = VentrovaColors.Muted)
        Text(text, style = bodyStyle(14.sp, color = color))
    }
}
